import { useState } from "react";
import { Helmet } from "react-helmet-async";
import fhcpPartnershipImg from "@/assets/fhcp-partnership.png";
import { motion } from "framer-motion";
import { Globe, MapPin, Mail, Rocket, Handshake } from "lucide-react";
import Navigation from "@/components/landing/Navigation";
import Footer from "@/components/landing/Footer";
import { useTranslation } from "@/hooks/useTranslation";

import { ContactModal } from "@/components/ContactModal";

const VidavoEU = () => {
  const {
    t
  } = useTranslation();
  const [contactOpen, setContactOpen] = useState(false);
  return <div className="min-h-screen bg-gradient-to-br from-neutral-100 to-neutral-50 relative overflow-hidden">
      <Helmet>
        <title>Vidavo - Digital Healthcare Solutions | Healthcare Anytime, Anywhere</title>
        <meta name="description" content="Vidavo EU expands digital health solutions in Europe. Based in Liège, Belgium, we offer remote care, clinical workflow management and healthcare innovation." />
        <meta name="keywords" content="digital health solutions Belgium" />
        <link rel="canonical" href="https://vidavo.eu/about/vidavo-eu-belgium" />
        
        {/* Open Graph */}
        <meta property="og:title" content="Digital Health Solutions in Europe | Vidavo EU" />
        <meta property="og:description" content="Vidavo EU brings digital health innovation to Europe from its new office in Liège, Belgium. Discover Vida24 and our growing network of healthcare collaborations." />
        <meta property="og:url" content="https://vidavo.eu/about/vidavo-eu-belgium" />
        <meta property="og:type" content="website" />
        
        {/* Twitter Card */}
        <meta name="twitter:title" content="Digital Health Solutions in Europe | Vidavo EU" />
        <meta name="twitter:description" content="Vidavo EU brings digital health innovation to Europe from its new office in Liège, Belgium. Discover Vida24 and our growing network of healthcare collaborations." />
      </Helmet>
      <Navigation />
      
      {/* Hero Section with Flowing Gradients */}
      <header className="relative min-h-[70vh] flex items-center justify-center pt-24 overflow-hidden">
        {/* Flowing background elements */}
        <div className="absolute top-0 right-0 w-1/2 h-[60%] bg-gradient-to-bl from-secondary/15 via-secondary/8 to-transparent blur-3xl" />
        <div className="absolute top-1/4 left-0 w-1/3 h-[50%] bg-gradient-to-r from-accent-teal/12 via-accent-teal/6 to-transparent blur-2xl" />
        <div className="absolute bottom-0 right-1/4 w-1/2 h-[40%] bg-gradient-to-t from-secondary/10 via-transparent to-transparent blur-3xl" />
        
        {/* Hero Content */}
        <div className="container-padding relative z-10 py-[5px] my-[50px]">
          <div className="max-w-5xl mx-auto text-center">
            {/* Badge */}
            <motion.div 
              className="inline-flex items-center gap-2 bg-gradient-to-r from-secondary/10 to-accent-teal/10 
                         border border-secondary/20 rounded-full px-6 py-3 text-secondary font-medium
                         backdrop-blur-sm mb-6"
              initial={{ opacity: 0, scale: 0.8 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.6, delay: 0.1 }}
            >
              <Globe className="w-4 h-4" />
              Vidavo EU
            </motion.div>
            
            <motion.h1 initial={{
            opacity: 0,
            y: 20
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            duration: 0.8,
            delay: 0.2
          }} className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl xl:text-6xl mb-6 sm:mb-8 text-accent-teal font-bold leading-tight px-4 sm:px-0">
              <span>{t.vidavoEU?.titlePart1}</span>{" "}
              <motion.span
                initial={{ opacity: 0, scale: 0.8 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ 
                  duration: 0.4, 
                  delay: 0.9, 
                  type: "spring",
                  stiffness: 400,
                  damping: 15
                }}
                className="inline-block text-secondary"
              >
                {t.vidavoEU?.titleHighlight}
              </motion.span>
            </motion.h1>
            
            <motion.p className="text-base sm:text-lg md:text-xl text-neutral-700 max-w-3xl mx-auto leading-relaxed mb-10" initial={{
            opacity: 0,
            y: 20
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            duration: 0.8,
            delay: 0.4
          }}>
              {t.vidavoEU?.subtitle}
            </motion.p>
            
            <motion.div initial={{
            opacity: 0,
            y: 20
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            duration: 0.8,
            delay: 0.6
          }}>
              <a 
                href="mailto:VidavoEU@vidavo.eu"
                className="button-primary group text-lg inline-block"
              >
                {t.vidavoEU?.ctaButton}
              </a>
            </motion.div>
          </div>
        </div>
      </header>

      <main className="py-16 relative overflow-hidden">
        {/* Flowing background elements */}
        <div className="absolute top-0 right-0 w-1/2 h-[60%] bg-gradient-to-bl from-secondary/15 via-secondary/8 to-transparent blur-3xl" />
        <div className="absolute top-1/4 left-0 w-1/3 h-[50%] bg-gradient-to-r from-accent-teal/12 via-accent-teal/6 to-transparent blur-2xl" />
        
        <div className="container-padding relative z-10">
          <div className="max-w-4xl mx-auto">

            {/* Placeholder Content Sections */}
            <div className="space-y-8">
            {/* Why Liège Section - What We Offer Style */}
            <motion.div 
              className="mb-8"
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.5 }}
            >
              {/* Centered Gradient Title */}
              <div className="text-center mb-8">
                <h2 className="text-3xl sm:text-4xl font-bold bg-gradient-to-r from-primary via-secondary to-accent-teal bg-clip-text text-transparent mb-4 py-[10px]">
                  {t.vidavoEU?.section1Title}
                </h2>
                <div className="w-20 h-1 bg-gradient-to-r from-accent-teal to-primary rounded-full mx-auto mb-6" />
                <p className="text-lg text-neutral-700 leading-relaxed max-w-3xl mx-auto">
                  {t.vidavoEU?.section1Intro}
                </p>
              </div>
              
              {/* Cards Grid */}
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                {[
                  { icon: Globe, color: "#A9E0DD", titleKey: 'card1Title', descKey: 'card1Desc' },
                  { icon: Rocket, color: "#66B3BB", titleKey: 'card2Title', descKey: 'card2Desc' },
                  { icon: Handshake, color: "#C8DDFF", titleKey: 'card3Title', descKey: 'card3Desc' }
                ].map((item, index) => (
                  <motion.div 
                    key={index}
                    className="group relative p-5 sm:p-6 rounded-2xl border border-white/20 shadow-lg 
                               hover:shadow-2xl transition-all duration-500 hover:-translate-y-1 overflow-hidden"
                    style={{ backgroundColor: item.color + '40' }}
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.6, delay: 0.7 + index * 0.1 }}
                  >
                    {/* Hover overlay */}
                    <div 
                      className="absolute inset-0 rounded-2xl opacity-0 group-hover:opacity-100 transition-opacity duration-500" 
                      style={{ backgroundColor: item.color + '60' }} 
                    />
                    
                    <div className="relative z-10">
                      {/* Icon box */}
                      <div 
                        className="w-14 h-14 sm:w-16 sm:h-16 rounded-2xl flex items-center justify-center 
                                   group-hover:scale-110 group-hover:rotate-3 transition-all duration-300 shadow-lg bg-white/30 mb-4"
                      >
                        <item.icon className="w-7 h-7 sm:w-8 sm:h-8 text-slate-800" />
                      </div>
                      
                      {/* Content */}
                      <h3 className="text-lg sm:text-xl font-medium mb-3 py-[5px] text-slate-800">
                        {t.vidavoEU?.section1Cards?.[item.titleKey as keyof typeof t.vidavoEU.section1Cards]}
                      </h3>
                      {t.vidavoEU?.section1Cards?.[item.descKey as keyof typeof t.vidavoEU.section1Cards] && (
                        <p className="text-sm sm:text-base leading-relaxed py-[2px] text-slate-700">
                          {t.vidavoEU?.section1Cards?.[item.descKey as keyof typeof t.vidavoEU.section1Cards]}
                        </p>
                      )}
                    </div>
                  </motion.div>
                ))}
              </div>
            </motion.div>

              <motion.div className="healthcare-card relative" initial={{
              opacity: 0,
              y: 30
            }} animate={{
              opacity: 1,
              y: 0
            }} transition={{
              duration: 0.8,
              delay: 0.6
            }}>
                <div className="absolute inset-0 bg-gradient-to-br from-accent-teal/5 to-secondary/5 rounded-2xl" />
                <div className="relative z-10 p-8">
                  <div className="flex items-center gap-4 mb-6">
                    <div className="p-3 rounded-2xl bg-gradient-to-r from-accent-teal to-secondary text-white">
                      <MapPin className="w-8 h-8" />
                    </div>
                    <div>
                      <h2 className="text-2xl font-bold text-primary">
                        {t.vidavoEU?.section2Title}
                      </h2>
                      <p className="text-neutral-600">{t.vidavoEU?.section2Subtitle}</p>
                    </div>
                  </div>
                  
                  {/* Contact Info - Two Colored Boxes */}
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
                    {/* Address Box - Light Teal */}
                    <div 
                      className="p-6 rounded-xl hover:scale-[1.02] transition-all duration-300 border border-white/20 shadow-lg hover:shadow-xl"
                      style={{ backgroundColor: '#A9E0DD40' }}
                    >
                      <div className="flex items-center gap-3 mb-3">
                        <MapPin className="w-6 h-6 text-slate-800" />
                        <p className="font-semibold text-lg text-slate-800">{t.vidavoEU?.section2AddressLabel}</p>
                      </div>
                      <a 
                        href="https://www.google.com/maps/search/?api=1&query=Quai+sur-Meuse+19,+4000+Liège,+Belgium" 
                        target="_blank" 
                        rel="noopener noreferrer"
                        className="text-slate-700 hover:text-slate-900 transition-colors"
                      >
                        {t.vidavoEU?.section2Address}
                      </a>
                    </div>
                    
                    {/* Email Box - Medium Teal */}
                    <a 
                      href="mailto:VidavoEU@vidavo.eu"
                      className="block p-6 rounded-xl hover:scale-[1.02] transition-all duration-300 border border-white/20 shadow-lg hover:shadow-xl cursor-pointer"
                      style={{ backgroundColor: '#66B3BB40' }}
                    >
                      <div className="flex items-center gap-3 mb-3">
                        <Mail className="w-6 h-6 text-slate-800" />
                        <p className="font-semibold text-lg text-slate-800">{t.vidavoEU?.section2EmailLabel}</p>
                      </div>
                      <span className="text-slate-700">
                        {t.vidavoEU?.section2Email}
                      </span>
                    </a>
                  </div>
                  
                  {/* Google Maps Embed - Clickable */}
                  <a 
                    href="https://www.google.com/maps/search/?api=1&query=Quai+sur-Meuse+19,+4000+Liège,+Belgium"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="block rounded-xl overflow-hidden mb-6 border border-neutral-200 relative group cursor-pointer"
                  >
                    {/* Clickable overlay */}
                    <div className="absolute inset-0 z-10 bg-transparent group-hover:bg-black/5 transition-colors duration-300" />
                    
                    <iframe
                      src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d2524.7956789!2d5.5740543!3d50.6425!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x47c0f9e9a0b0c0c0%3A0x0!2sQuai%20sur-Meuse%2019%2C%204000%20Li%C3%A8ge%2C%20Belgium!5e0!3m2!1sen!2sgr!4v1234567890"
                      width="100%"
                      height="300"
                      style={{ border: 0, pointerEvents: 'none' }}
                      allowFullScreen
                      loading="lazy"
                      referrerPolicy="no-referrer-when-downgrade"
                      title="Vidavo EU Office Location"
                    />
                  </a>
                  
                  {/* Footer text */}
                  <p className="text-lg text-neutral-700 leading-relaxed italic">
                    {t.vidavoEU?.section2Footer}
                  </p>
                </div>
              </motion.div>
            </div>
          </div>
        </div>
      </main>

      {/* Vida24 Section - Full Video Background */}
      <section className="py-20 relative overflow-hidden min-h-[600px] flex items-center">
        {/* Video Background */}
        <video
          src="/videos/vida24-telemedicine.mp4"
          autoPlay
          loop
          muted
          playsInline
          className="absolute inset-0 w-full h-full object-cover"
        />
        
        {/* Dark Overlay for text readability */}
        <div className="absolute inset-0 bg-gradient-to-r from-black/80 via-black/60 to-black/40" />
        
        {/* Content on top */}
        <div className="container-padding relative z-10 w-full">
          <motion.div 
            className="max-w-3xl space-y-6"
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
          >
            {/* Vida24 Logo - inverted for dark background */}
            <img 
              src="/lovable-uploads/vida24LOGO_4.png" 
              alt="Vida24" 
              className="h-20 mb-8" 
            />
            
            {/* Title */}
            <h2 className="text-3xl md:text-4xl font-bold text-white">
              {t.vidavoEU?.vida24Title}
            </h2>
            
            {/* Intro */}
            <p className="text-lg text-white/90 leading-relaxed">
              {t.vidavoEU?.vida24Intro}
            </p>
            
            {/* Benefits with accent bars */}
            <ul className="space-y-4">
              {t.vidavoEU?.vida24Benefits?.map((benefit, index) => (
                <li key={index} className="flex items-start gap-4">
                  <div className="w-1 h-6 bg-secondary rounded-full flex-shrink-0 mt-0.5" />
                  <span className="text-white/85">{benefit}</span>
                </li>
              ))}
            </ul>
            
            {/* Closing */}
            <p className="text-lg text-white/90 leading-relaxed font-medium border-l-4 border-accent-teal pl-4">
              {t.vidavoEU?.vida24Closing}
            </p>
          </motion.div>
        </div>
      </section>

      {/* European Collaborations - Clean, Centered Section */}
      <section className="py-20">
        <div className="container-padding">
          <div className="max-w-3xl mx-auto text-center">
            
            {/* Decorative top line */}
            <div className="flex justify-center mb-8">
              <div className="w-24 h-1 bg-gradient-to-r from-accent-teal to-secondary rounded-full" />
            </div>
            
            {/* Centered Title */}
            <motion.h2 
              className="text-3xl md:text-4xl font-bold text-primary mb-8"
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
            >
              {t.vidavoEU?.euroCollabTitle}
            </motion.h2>
            
            {/* Description */}
            <motion.p 
              className="text-lg text-neutral-700 leading-relaxed mb-6"
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: 0.1 }}
            >
              {t.vidavoEU?.euroCollabDescription}
            </motion.p>
            
            {/* Partnership note */}
            <motion.h3 
              className="text-2xl md:text-3xl font-bold text-primary mt-8"
              initial={{ opacity: 0 }}
              whileInView={{ opacity: 1 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: 0.3 }}
            >
              {t.vidavoEU?.euroCollabNote}
            </motion.h3>
            
            {/* Partnership image */}
            <motion.div
              className="mt-6"
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: 0.4 }}
            >
              <img 
                src={fhcpPartnershipImg} 
                alt="Strategic Partnership between Vidavo and FHC+P Consulting GmbH" 
                className="w-full max-w-2xl mx-auto rounded-2xl shadow-lg"
              />
            </motion.div>
            
            {/* Decorative bottom line */}
            <div className="flex justify-center mt-8">
              <div className="w-24 h-1 bg-gradient-to-r from-secondary to-accent-teal rounded-full" />
            </div>
          </div>
        </div>
      </section>

      {/* Get in Touch CTA Section */}
      <section className="py-20 bg-gradient-to-br from-accent-teal/5 via-background to-secondary/5">
        <div className="container-padding">
          <div className="max-w-3xl mx-auto text-center">
            
            <motion.h2 
              className="text-3xl md:text-4xl font-bold text-primary mb-6"
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
            >
              {t.vidavoEU?.getInTouchTitle}
            </motion.h2>
            
            <motion.p 
              className="text-lg text-foreground/80 leading-relaxed mb-4"
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: 0.1 }}
            >
              {t.vidavoEU?.getInTouchDescription}
            </motion.p>
            
            <motion.p 
              className="text-base text-muted-foreground mb-10"
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: 0.2 }}
            >
              {t.vidavoEU?.getInTouchSubtext}
            </motion.p>
            
            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              whileInView={{ opacity: 1, scale: 1 }}
              viewport={{ once: true }}
              transition={{ delay: 0.3, type: "spring", stiffness: 400, damping: 15 }}
            >
              <a 
                href="mailto:VidavoEU@vidavo.eu"
                className="button-primary group text-lg inline-block"
              >
                {t.vidavoEU?.getInTouchCTA}
              </a>
            </motion.div>
          </div>
        </div>
      </section>
      
      <Footer />
      
      <ContactModal open={contactOpen} onOpenChange={setContactOpen} />
    </div>;
};
export default VidavoEU;